import React, { useCallback, useEffect, useState } from 'react';
import { Topbar, CalendarModal } from '@components';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
    Radio,
    Spin,
} from 'antd';
import {
    AmountText,
    DateText,
    Image,
    ItemText,
    ListItem,
    SpinWrapper,
    StatusText,
    Wrapper,
} from '../Wallet/Wallet.style';
import { ListWrapper } from '../Wallet/Wallet.style';
import { CalendarFilled } from '@assets';
import { PRIVATE_ROUTE } from '@router/route.constants';
import { api } from '@api';
import {
    LIMITS,
    TOPUP_SK,
    WITHDRAW_SK,
    BONUS_SK,
} from '@constants';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getFormattedNumber } from '@utils';
import moment from 'moment';
import { BonusImage } from '../Wallet/Wallet';
import { useTranslation } from 'react-i18next';
import { getStatus } from '@utils';

const RootContainer = styled.div`
padding: 24px 16px;
height: calc(100vh - 118px);
overflow: hidden;

.ant-radio-wrapper {
    margin-right: 12px;
    padding-bottom: 10px;
    
    span {
        font-size: 16px;
        font-weight: 500;
        color: #000;
    }
}

.anticon .anticon-calendar {
    font-size: 28px;
    color: #fff;
}

@media screen and (max-width: 416px) {
    .ant-radio-wrapper span {
        font-size: 14px;
        padding-inline-end: 0;
    }
}

@media screen and (max-width: 365px) {
    padding: 24px 12px;

    .ant-radio-wrapper {
        margin-right: 6px;
    }

    .ant-radio-wrapper span {
        font-size: 13px;
        padding-inline-end: 0;
        padding-inline-start: 4px;
    }
}
`;

export const EmptyText = styled.div`
height: calc(100vh - 130px);
display: flex;
align-items: center;
justify-content: center;
text-align: center;
font-size: 13px;
`;

const StyledListWrapper = styled(ListWrapper).attrs({
    id: 'scrollable-div',
})`
height: calc(100vh - 140px);
padding-top: 0 !important;
`;

const {
    DEFAULT_PAGE,
    PAGE_SIZE,
} = LIMITS;

const dateFormat = 'MMM DD, YYYY hh:mm:ss A';
const apiDateFormat = 'YYYY-MM-DD';

const TransactionHistory = () => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [type, setType] = useState('');
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [total, setTotal] = useState(0);
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState('');
    const [loading, setLoading] = useState(true);

    const getTransactions = useCallback(() => {
        setLoading(true);
        api
            .getTransactions({
                page,
                limit: PAGE_SIZE,
                type,
                start_date: date,
                end_date: date,
            })
            .then(({ transactions, pagination }) => {
                setData(prevState => {
                    return [
                        ...prevState,
                        ...transactions,
                    ];
                });
                setPage(pagination.page);
                setTotal(pagination.total);
                setLoading(false);
            })
            .catch(() => { });

    }, [type, page, date]);

    useEffect(() => {
        getTransactions();

    }, [getTransactions]);

    const onChange = (e) => {
        setType(e.target.value);
        setPage(DEFAULT_PAGE);
        setData([]);
        setTotal(0);
    };

    const onOk = (newdate) => {
        newdate = newdate.format(apiDateFormat);
        if (newdate !== date) {
            setDate(newdate);
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
        handleClose();
    }

    const onReset = () => {
        if (date) {
            setDate('');
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
    }

    const handleClose = () => {
        setOpen(false);
    }

    const goBack = () => {
        navigate(`/${PRIVATE_ROUTE.WALLET}`);
    }

    const renderTop = () => (
        <Radio.Group onChange={onChange} value={type}>
            <Radio value={''}>{t("wallet.transactionHistoryHeadingAll")}</Radio>
            <Radio value={WITHDRAW_SK}>{t('wallet.walletWithdraw')}</Radio>
            <Radio value={TOPUP_SK}>{t('wallet.walletTopUp')}</Radio>
            <Radio value={BONUS_SK}>{t("wallet.transactionHistoryHeadingBonus")}</Radio>
        </Radio.Group>
    );

    const Loader = () => (
        <SpinWrapper>
            <Spin />
        </SpinWrapper>
    );

    return (
        <>
            <Topbar
                title={t("wallet.transactionHistoryTitle")}
                rightIcon={
                    <CalendarFilled onClick={() => setOpen(true)} />
                }
                goBack={goBack}
            />
            <RootContainer>
                {!loading && renderTop()}
                <StyledListWrapper>
                    <InfiniteScroll
                        dataLength={data.length}
                        next={() => setPage((prev) => prev + 1)}
                        hasMore={loading || data.length < total}
                        loader={<Loader />}
                        scrollableTarget='scrollable-div'
                        endMessage={!loading && data.length === 0 && <EmptyText>{t("common.noRecord")}</EmptyText>}
                    >
                        {data.map((item, key) => (
                            <ListItem key={key}>
                                {item.type === BONUS_SK
                                    ? <BonusImage />
                                    : <Image
                                        src={item.payment_category_image}
                                        radius={item.payment_category_name === 'KBZ' ? 'none' : 'circle'}
                                    />
                                }
                                <Wrapper>
                                    <ItemText>
                                        {item.type === BONUS_SK
                                            ? t("wallet.transactionHistoryBonusType", { bonus_type: item.bonus_type.replaceAll('-bonus',''), })
                                            : `${item.type === WITHDRAW_SK ? t('wallet.walletWithdraw') : t('wallet.walletTopUp')} ${t("common.to")} ${item.num}`
                                        }
                                    </ItemText>
                                    <DateText>{moment.unix(item.created_at).format(dateFormat)}</DateText>
                                    {item.trx_num && <DateText>{`PIN: ${item.trx_num}`}</DateText>}
                                </Wrapper>
                                <Wrapper>
                                    <AmountText>{`${getFormattedNumber(item.amount)} MMK`}</AmountText>
                                    {item.type !== BONUS_SK &&
                                        <StatusText status={item.status}>
                                            {getStatus(item.status)}
                                        </StatusText>}
                                </Wrapper>
                            </ListItem>
                        ))}
                    </InfiniteScroll>
                </StyledListWrapper>
            </RootContainer>
            <CalendarModal
                open={open}
                date={date}
                onOk={onOk}
                onReset={onReset}
                handleClose={handleClose}
            />
        </>
    );
}

export default TransactionHistory;